import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline as UIcon, List, ListOrdered, Heading2, Heading3, Table as TIcon, Undo2, Redo2, Pilcrow } from "lucide-react";

const Btn = ({ on, active, label, children }) => (
  <button type="button" onMouseDown={(e) => { e.preventDefault(); on(); }} aria-label={label} title={label}
    className={`rounded-md p-1.5 hover:bg-muted ${active ? "bg-secondary text-primary" : "text-foreground"}`}>{children}</button>
);

export function RichEditor({ value, onChange, disabled, placeholder = "Write in your own format. Type, paste from Word or Google Docs, use headings, lists, and tables." }) {
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] } }), Underline, Placeholder.configure({ placeholder }),
      Table.configure({ resizable: false }), TableRow, TableHeader, TableCell],
    content: value || "", editable: !disabled,
    editorProps: { attributes: { class: "prose max-w-none text-sm leading-relaxed", "data-testid": "rich-editor" },
      transformPastedHTML: (h) => DOMPurify.sanitize(h, { ALLOWED_TAGS: ["p", "br", "b", "strong", "i", "em", "u", "h1", "h2", "h3", "ul", "ol", "li", "table", "tbody", "thead", "tr", "td", "th"], ALLOWED_ATTR: [] }) },
    onUpdate: ({ editor: e }) => { if (e.isEditable) onChange?.(e.getHTML()); },
  });
  useEffect(() => { if (editor && value !== undefined && value !== editor.getHTML() && !editor.isFocused) editor.commands.setContent(value || "", false); }, [value, editor]);
  useEffect(() => { editor?.setEditable(!disabled); }, [disabled, editor]);
  if (!editor) return null;
  return (
    <div className="rounded-[12px] border border-border bg-card">
      {!disabled && (
        <div className="flex flex-wrap items-center gap-0.5 border-b border-border p-1.5" role="toolbar" aria-label="Formatting">
          <Btn label="Paragraph" on={() => editor.chain().focus().setParagraph().run()} active={editor.isActive("paragraph")}><Pilcrow className="h-4 w-4" /></Btn>
          <Btn label="Heading" on={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })}><Heading2 className="h-4 w-4" /></Btn>
          <Btn label="Subheading" on={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })}><Heading3 className="h-4 w-4" /></Btn>
          <span className="mx-1 h-5 w-px bg-border" />
          <Btn label="Bold" on={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")}><Bold className="h-4 w-4" /></Btn>
          <Btn label="Italic" on={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")}><Italic className="h-4 w-4" /></Btn>
          <Btn label="Underline" on={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")}><UIcon className="h-4 w-4" /></Btn>
          <span className="mx-1 h-5 w-px bg-border" />
          <Btn label="Bullet list" on={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")}><List className="h-4 w-4" /></Btn>
          <Btn label="Numbered list" on={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")}><ListOrdered className="h-4 w-4" /></Btn>
          <Btn label="Insert table" on={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}><TIcon className="h-4 w-4" /></Btn>
          <span className="mx-1 h-5 w-px bg-border" />
          <Btn label="Undo" on={() => editor.chain().focus().undo().run()}><Undo2 className="h-4 w-4" /></Btn>
          <Btn label="Redo" on={() => editor.chain().focus().redo().run()}><Redo2 className="h-4 w-4" /></Btn>
        </div>)}
      <div className="p-4"><EditorContent editor={editor} /></div>
    </div>
  );
}

export function insertHtml(editorHtml, snippet) {
  return `${editorHtml || ""}<p>${snippet}</p>`;
}
